const DEFAULT_PROBE_INTERVAL_MS = 2 * 60 * 1000
const PROBE_JITTER_RATIO = 0.1

export function readFrontendGeneration(documentHtml) {
  const parsedDocument = new DOMParser().parseFromString(
    documentHtml,
    'text/html'
  )
  const marker = parsedDocument.querySelector(
    'meta[name="okku-frontend-generation"]'
  )
  const generation = marker && marker.getAttribute('content')

  return generation === 'vue2' || generation === 'vue3'
    ? generation
    : null
}

async function fetchCurrentRouteDocument() {
  const response = await fetch(
    `${window.location.pathname}${window.location.search}`,
    {
      cache: 'no-store',
      credentials: 'same-origin'
    }
  )

  if (!response.ok) {
    throw new Error(
      `Route generation probe failed with status ${response.status}`
    )
  }

  return response.text()
}

export function createPublicScreenRouteWatcher(options = {}) {
  const fetchDocument =
    options.fetchDocument || fetchCurrentRouteDocument
  const generation = options.generation || 'vue2'
  const probeIntervalMs =
    options.probeIntervalMs || DEFAULT_PROBE_INTERVAL_MS
  const reloadPage =
    options.reloadPage || (() => window.location.reload())

  let isStarted = false
  let probeTimer = null

  function clearTimer(timer) {
    if (timer !== null) clearTimeout(timer)
  }

  function nextProbeDelay() {
    const jitter =
      probeIntervalMs * PROBE_JITTER_RATIO * Math.random()
    return probeIntervalMs + jitter
  }

  function scheduleProbe(delay = nextProbeDelay()) {
    clearTimer(probeTimer)
    probeTimer = setTimeout(() => checkNow(), delay)
  }

  async function checkNow() {
    if (!isStarted) return

    clearTimer(probeTimer)
    probeTimer = null

    try {
      const documentHtml = await fetchDocument()
      if (!isStarted) return

      const servedGeneration = readFrontendGeneration(documentHtml)

      if (
        servedGeneration !== null &&
        servedGeneration !== generation
      ) {
        stop()
        reloadPage()
        return
      }
    } catch (error) {
      console.warn(
        'Public screen route probe failed; keeping the current screen active.',
        error
      )
    }

    if (isStarted) scheduleProbe()
  }

  function handleOnline() {
    checkNow()
  }

  function handleVisibilityChange() {
    if (document.visibilityState === 'visible') checkNow()
  }

  function start() {
    if (isStarted) return

    isStarted = true
    window.addEventListener('online', handleOnline)
    document.addEventListener(
      'visibilitychange',
      handleVisibilityChange
    )
    scheduleProbe()
  }

  function stop() {
    isStarted = false
    clearTimer(probeTimer)
    probeTimer = null
    window.removeEventListener('online', handleOnline)
    document.removeEventListener(
      'visibilitychange',
      handleVisibilityChange
    )
  }

  return { checkNow, start, stop }
}
